"use client";

import { useMemo, useSyncExternalStore } from "react";
import { subscribeHostNavigation } from "../host-navigation";
import { isTrustedMessageSource } from "../message-source";
import { useWidgetEnv } from "../use-widget-env";
import {
  applyLocationOverride,
  type HostLocation,
  type LocationOverride,
  parseHostLocation,
} from "./host-location";

interface Store {
  subscribe: (cb: () => void) => () => void;
  getSnapshot: () => string;
}

function domStore(): Store {
  return {
    subscribe: subscribeHostNavigation,
    getSnapshot: () => window.location.href,
  };
}

/**
 * iframe: the loader passes `?referer=` and forwards each navigation as a message. ONE store per frame
 * (per `?referer=` value), listening from its creation: a component that mounts after a navigation
 * (the Messages tab opened later) must see the CURRENT host location, not the load-time referer.
 */
let iframeShared: { key: string; store: Store } | null = null;

function iframeStore(): Store {
  const key = new URLSearchParams(window.location.search).get("referer") ?? "";
  if (iframeShared?.key === key) return iframeShared.store;
  let href = key;
  const listeners = new Set<() => void>();
  window.addEventListener("message", (e: MessageEvent) => {
    if (!isTrustedMessageSource(e, "iframe")) return;
    const d = e.data;
    if (!d || typeof d !== "object" || d.type !== "wexio:host-navigation:v1") return;
    if (typeof d.url !== "string" || d.url === href) return;
    href = d.url;
    for (const cb of listeners) cb();
  });
  const store: Store = {
    subscribe(cb) {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    getSnapshot: () => href,
  };
  iframeShared = { key, store };
  return store;
}

const SERVER_SNAPSHOT = () => "";

/**
 * The host page location the targeting matcher runs against. ONE source for welcome and
 * Home picking. In-DOM embeds read `window.location` and share the tracking provider's history
 * observer; the iframe reads `?referer=` plus `wexio:host-navigation:v1`. The returned object is
 * stable until the location (or override) changes. `override` is the private `audienceOverride`
 * origin/path (matching only).
 */
export function useHostLocation(override?: LocationOverride): HostLocation {
  const env = useWidgetEnv();
  const store = useMemo(
    () =>
      typeof window === "undefined" ? null : env.kind === "iframe" ? iframeStore() : domStore(),
    [env.kind],
  );
  const href = useSyncExternalStore(
    store?.subscribe ?? (() => () => {}),
    store?.getSnapshot ?? SERVER_SNAPSHOT,
    SERVER_SNAPSHOT,
  );
  const overrideOrigin = override?.origin;
  const overridePath = override?.path;
  return useMemo(
    () =>
      applyLocationOverride(parseHostLocation(href), {
        origin: overrideOrigin,
        path: overridePath,
      }),
    [href, overrideOrigin, overridePath],
  );
}
